/**
 * Изменение ресурсов платформы (Kubernetes-кластер, PostgreSQL, Kafka): кнопка «Изменить» ведёт на
 * /resource-edit?entityRef=…, та берёт текущие параметры у бэкенд-плагина viva-edit и открывает
 * скрытый шаблон <тип>-edit с ними в formData (так же, как кнопка обновления GitLab подставляет версию).
 */
import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import { Entity } from '@backstage/catalog-model';
import { MANAGED_SERVICES, managedServiceOf } from './viva';

/** Типы Resource, у которых есть шаблон изменения. */
export const EDITABLE_RESOURCE_TYPES = [
  'kubernetes-cluster',
  'postgres-cluster',
  'kafka-cluster',
  'mongodb-cluster',
  'rabbitmq-cluster',
  'valkey-cluster',
];

export const isEditableResource = (entity: Entity): boolean =>
  entity.kind === 'Resource' && EDITABLE_RESOURCE_TYPES.includes(String(entity.spec?.type ?? ''));

/** Шаблоны изменения (тег edit) не показываются в «Создать» — только через кнопку на странице ресурса. */
export const isEditTemplate = (template: Entity): boolean =>
  (template.metadata.tags ?? []).includes('edit');

/** Скрытые на этом стенде шаблоны (тег hidden ставит entrypoint образа, например без cloud.ru или VK Cloud). */
export const isHiddenTemplate = (template: Entity): boolean =>
  (template.metadata.tags ?? []).includes('hidden');

export const resourceEditUrl = (entityRef: string): string =>
  `/resource-edit?entityRef=${encodeURIComponent(entityRef)}`;

export const editTemplateUrl = (template: string, formData: Record<string, unknown>): string =>
  `/create/templates/default/${template}?formData=${encodeURIComponent(JSON.stringify(formData))}`;

/**
 * Managed-хранилища облаков (cloud.ru — managed-<тип>, VK Cloud — vkcloud-<тип>): один шаблон удаления на
 * все, облако и тип — поля target и kind формы.
 */
const MANAGED_DELETE_TEMPLATE = 'managed-datastore-delete';

/** Типы Resource с кнопкой «Удалить» (шаблон <тип>-delete; managed-хранилища — общий). */
export const DELETABLE_RESOURCE_TYPES = ['valkey-cluster', ...MANAGED_SERVICES.map(s => s.type)];

export const isDeletableResource = (entity: Entity): boolean =>
  entity.kind === 'Resource' && DELETABLE_RESOURCE_TYPES.includes(String(entity.spec?.type ?? ''));

/**
 * Шаблон удаления с уже подставленными именем хранилища и кластером (как в форме изменения:
 * cluster — ref ресурса кластера). Имя — без префикса типа (valkey-cache -> cache).
 */
export const resourceDeleteUrl = (entity: Entity): string => {
  const type = String(entity.spec?.type ?? '');
  const name =
    entity.metadata.annotations?.['viva.company.com/datastore-name'] ??
    entity.metadata.name.replace(new RegExp(`^${type.replace(/-cluster$/, '')}-`), '');
  const cluster = entity.metadata.annotations?.['viva.company.com/cluster'] ?? '';
  const managed = managedServiceOf(type);
  if (managed) {
    return editTemplateUrl(MANAGED_DELETE_TEMPLATE, {
      target: managed.cloud,
      name,
      kind: managed.kind,
      cluster: `resource:default/${cluster}`,
    });
  }
  return editTemplateUrl(`${type}-delete`, { name, cluster: `resource:default/${cluster}` });
};

export type EditForm = { template: string; formData: Record<string, unknown> };

export async function fetchEditForm(
  apis: { discoveryApi: DiscoveryApi; fetchApi: FetchApi },
  entityRef: string,
): Promise<EditForm> {
  const base = await apis.discoveryApi.getBaseUrl('viva-edit');
  const response = await apis.fetchApi.fetch(`${base}/form?entityRef=${encodeURIComponent(entityRef)}`);
  if (!response.ok) {
    let message = `HTTP ${response.status}`;
    try {
      message = (await response.json())?.error?.message ?? message;
    } catch {
      // тело не JSON — остаётся код ответа
    }
    throw new Error(`Текущие параметры не прочитаны: ${message}`);
  }
  return response.json();
}
